<template>
  <form id="location-search-form" class="vue-app">
    <strong class="h5 d-block">Search for a Location</strong>
    <div class="form-row">
      <div class="col-12 col-md-8">
        <div class="form-group">
          <input id="postal_code" type="text" class="form-control"
          placeholder="City or ZIP Code" v-model="form.postal_code" ref="postal_code" :disabled="form.using_geolocation">
        </div>
      </div>
      <div class="col-12 col-md-4">
        <div class="form-group">
          <button type="button" @click.prevent="getUserLocation()"
          class="btn btn-primary location-btn w-100"
          v-bind:class="{'using-geolocation': form.using_geolocation}">Detect My Location</button>
        </div>
      </div>
    </div>
    <div class="form-row">
      <div class="col-12 col-md-8">
        <div class="form-group">
          <multiselect
            id="search_county"
            v-model="form.county"
            :options="counties"
            :multiple="true"
            placeholder="County"
            :close-on-select="false"
            :clear-on-select="true"
            selectLabel="Press enter or click to select"
            deselectLabel="Press enter or click to remove"
            label="text"
            track-by="id"
            :maxHeight="dropdownHeight"
            @open="onMultiselectOpen"
            @close="onMultiselectClose"
            ref="search_county"
          >
          </multiselect>
        </div>
      </div>
      <div class="col-12 col-md-4">
        <div class="form-group">
          <multiselect
            id="search_radius"
            v-model="form.radius"
            :options="radii"
            :close-on-select="true"
            :search="false"
            label="text"
            track-by="id"
            :maxHeight="dropdownHeight"
            @open="onMultiselectOpen"
            @close="onMultiselectClose"
            ref="search_radius"
          ></multiselect>
        </div>
      </div>
    </div>
    <div class="form-group">
      <div class="form-row">
        <div class="col-12">
          <input id="search" placeholder="Location Name, Services, Description" type="text" class="form-control" v-model="form.search" ref="search"/>
        </div>
      </div>
    </div>
    <div class="form-group">
      <div class="form-row">
        <div class="col-12">
          <button type="submit" @click.prevent="searchLocations(true)" class="btn btn-primary mr-1">Search</button>
          <button type="button" @click.prevent="resetForm()" class="btn btn-dark">Reset Form</button>
        </div>
      </div>
    </div>
  </form>
</template>

<script>
import { Loader } from '@googlemaps/js-api-loader';
import { mapMutations } from 'vuex';
import ApiCalls from "../../api/apiServices"

const METERS_PER_MILE = 1609.34;
const MULTISELECT_DELIMITER = ',';
const MUTLISELECT_FIELDS = ['county'];

const locations_map = document.querySelector('.locations-gmap')
const result_items = document.querySelectorAll('.location-result');
let map, markers, infoWindow, geocoder;

if (locations_map) {
  document.addEventListener('DOMContentLoaded', () => {
    // set up initial gmaps
    const loader = new Loader({
      apiKey: window.gmapi,
      version: 'weekly',
      libraries: ['geometry']
    });
    loader.load().then(() => {
      geocoder = new google.maps.Geocoder();
      const result_arr = [...result_items];

      // calculate the center of all available locations
      let lat_max = Math.max(...result_arr.map((a) => parseFloat(a.getAttribute('data-lat'))));
      let lat_min = Math.min(...result_arr.map((a) => parseFloat(a.getAttribute('data-lat'))));
      let lng_max = Math.max(...result_arr.map((a) => parseFloat(a.getAttribute('data-lng'))));
      let lng_min = Math.min(...result_arr.map((a) => parseFloat(a.getAttribute('data-lng'))));
      const loc = {
        lat: (lat_max + lat_min) / 2,
        lng: (lng_max + lng_min) / 2
      };

      map = new google.maps.Map(locations_map, {
        center: loc,
        zoom: parseInt(locations_map.getAttribute('data-zoom')) || 8
      });

      infoWindow = new google.maps.InfoWindow({})


      markers = [...result_items].map((elem, ind) => {
        const marker = new google.maps.Marker({
          position: {
            lat: parseFloat(elem.getAttribute('data-lat')),
            lng: parseFloat(elem.getAttribute('data-lng'))
          },
          map: map,
          label: {
            text: elem.getAttribute('data-id'),
            color: 'white',
            fontWeight: '700',
            fontFamily: 'Nunito Sans'
          },
          icon: {
            url: window.theme_path + "/frontend/dist/images/icons/location-icon.png",
            size: new google.maps.Size(34, 49),
            origin: new google.maps.Point(0,0),
            anchor: new google.maps.Point(17, 49),
            labelOrigin: new google.maps.Point(17, 17)
          }
        });

        marker.addListener('click', () => {
          infoWindow.setContent(elem.querySelector('.location-result-bubble').outerHTML);
          infoWindow.open(map, marker);
          elem.parentNode.scrollTop = elem.offsetTop;
        })

        elem.setAttribute('data-marker', ind);

        return marker;
      });

      return markers;
    }).then((markers) => {
      document.dispatchEvent(new Event('WFMapReady'));
    });
  });
}

export default {
  data() {
    let params = new URLSearchParams(window.location.search);
    let form = {};
    let radii = this.getRadiusOptions();
    ['search', 'postal_code', 'lat', 'long', 'using_geolocation'].forEach((key) => {
      form[key] = params.get('s_' + key) ? params.get('s_' + key) : null
    });
    // set default arrays for mutli-selects
    MUTLISELECT_FIELDS.forEach((key) => {
      form[key] = [];
    });

    form.radius = params.get('s_radius') ? radii.filter((a) => a.id == parseInt(params.get('s_radius')))[0] : radii[1];


    return {
      form: form,
      counties: [],
      radii: radii,
      dropdownHeight: 200,
    }
  },
  methods: {
    getRadiusOptions() {
      return [
        { id: 10, text: "Within 10 miles"},
        { id: 20, text: "Within 20 miles"},
        { id: 50, text: "Within 50 miles"},
        { id: 100, text: "Within 100 miles"},
      ];
    },
    getUserLocation() {
      this.form.using_geolocation = !this.form.using_geolocation;
      if (!this.form.using_geolocation) {
        this.form.lat = null;
        this.form.long = null;
      } else {
        if (navigator.geolocation) {
          navigator.geolocation.getCurrentPosition((position) => {
            this.form.lat = position.coords.latitude;
            this.form.long = position.coords.longitude;
          });
          this.form.postal_code = null;
        } else {
          this.form.using_geolocation = false;
        }
      }
    },
    findReferenceLocation() {
      return new Promise((resolve, reject) => {
        let corrected_postal_city_field = ""
        if (this.form.lat) {
          resolve(new google.maps.LatLng({
            lat: this.form.lat,
            lng: this.form.long
          }))
        } else if (this.form.postal_code) {
          if(isNaN(this.form.postal_code)){
            corrected_postal_city_field = this.form.postal_code + " FL"
          } else {
            corrected_postal_city_field = this.form.postal_code
          }
          geocoder.geocode({ address: corrected_postal_city_field }, (results, status) => {
            if (status == 'OK') {
              resolve(results[0].geometry.location);
            } else {
              reject("Geocoder status: " + status);
            }
          })
        } else {
          resolve(0);
        }
      });
    },
    getCountyValues() {
      return this.form.county.map((a) => a.value);
    },
    searchLocations(scroll = false) {
      if (locations_map) {
        result_items.forEach((elem) => {
          elem.classList.add('d-none');
        })
        markers.forEach((mrk) => {
          mrk.setMap(null);
        })
        this.findReferenceLocation().then((refLoc) => {
          let count = 0;
          [...result_items].filter((a) => {
            let accept = true;
            if (refLoc) {
              let dist = google.maps.geometry.spherical.computeDistanceBetween(refLoc, new google.maps.LatLng({
                lat: parseFloat(a.getAttribute('data-lat')),
                lng: parseFloat(a.getAttribute('data-lng'))
              }));
              a.setAttribute('data-distance', dist);
              a.setAttribute('data-distance-mi', dist / METERS_PER_MILE);
              if (dist > this.form.radius.id * METERS_PER_MILE) accept = false;
            }
            if (this.form.county.length && !this.getCountyValues().includes(a.getAttribute('data-county'))) {
              accept = false;
            }
            if (this.form.search) {
              let terms = this.form.search.split(' ');
              terms.forEach((t) => {
                if (a.getAttribute('data-search').toLowerCase().indexOf(t.toLowerCase()) < 0) accept = false
              })
            }
            return accept;
          }).sort((a,b) => {
            // sort by distance from reference, if we're using reference, otherwise use the default sort
            if (refLoc) {
              return parseFloat(a.getAttribute('data-distance')) - parseFloat(b.getAttribute('data-distance'));
            } else {
              return parseInt(a.getAttribute('data-id')) - parseInt(b.getAttribute('data-id'));
            }
          }).forEach((elem, ind) => {
            count++;
            elem.classList.remove('d-none');
            elem.parentNode.insertBefore(elem, elem.parentNode.childNodes[ind]);
            markers[parseInt(elem.getAttribute('data-marker'))].setMap(map)
          });
          if (count) {
            document.querySelector(".no-results").classList.add("d-none");
          } else {
            document.querySelector(".no-results").classList.remove("d-none");
          }
          if (scroll === true) {
            document.querySelector('.location-listings').scrollIntoView({ behavior: 'smooth', block: 'start' });
          }
        }, () => {
          // geocoding error
        })
      } else {
        let params = new URLSearchParams();
        for (let key in this.form) {
          if (this.form[key]) {
            let val = this.form[key]
            if (key == 'county') {
              val = val.map((a) => a.value).join(MULTISELECT_DELIMITER)
            }
            if (key == 'radius') {
              val = val.id;
            }
            params.set('s_' + key, val)
          }
        }
        window.location = "/locations/?" + params.toString();
      }
    },

    retrieveDropdowns() {
      this.getDropdownValues('counties').then((data) => {
        if (data != null) {
          [...data].forEach((result) => {
            let text = result.text;
            let slug = result.slug;
            let type = {
              text: text,
              value: slug,
              id: slug,
              disabled: false
            };
            this.counties.push(type);
          });
          let params = new URLSearchParams(window.location.search);
          if (params.get('s_county')) {
            let countyval = params
              .get('s_county')
              .split(MULTISELECT_DELIMITER)
              .map((a) => { 
                return this.counties.find((b) => b.value == a );
              });
            this.form.county = countyval;
          }
        }
        if (locations_map) {
          document.addEventListener("WFMapReady", this.searchLocations)
        }
      });
    },
    getDropdownValues(type){
        return new Promise((resolve, reject) => {
            resolve(window[type])
        })
    },
    resetForm() {
      let form = {};
      for (let key in this.form) {
        form[key] = null;
      }
      // set default arrays for mutli-selects
      MUTLISELECT_FIELDS.forEach((key) => {
        form[key] = [];
      });
      form.radius = this.radii[1];
      form.using_geolocation = false;
      // document.querySelector('.location-btn').classList.remove('using-geolocation');
      this.form = form;
    },

        findSpacingTarget() {
            return this.$el.closest(".form-container")
        },
        findPositionTop(elem) {
            let offsetTop = 0;
            do {
                offsetTop += elem.offsetTop
            } while (elem = elem.offsetParent)
            return offsetTop;
        },

        onMultiselectOpen(id) {
            let spacingTarget = this.findSpacingTarget()
            if (spacingTarget) {
                let dropdownField = this.$el.querySelector("#" + id).closest('.multiselect')
                let ddtop = this.findPositionTop(dropdownField) - this.findPositionTop(spacingTarget)
                spacingTarget.style.marginBottom = Math.max(0, (ddtop + dropdownField.offsetHeight + this.dropdownHeight) - spacingTarget.offsetHeight) + "px"
            }
        },

        onMultiselectClose() {
            let spacingTarget = this.findSpacingTarget()
            if (spacingTarget) {
                spacingTarget.style.marginBottom = 0
            }
        },
  },
  created() {
    this.retrieveDropdowns()
  },
  mounted() {
    ["search_county", "search_radius"].forEach((item) => {
      this.$refs[item].$refs.search.setAttribute("autocomplete", "off");
    })
  }
}
</script>